import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CardIcon } from '../../../assets/icons/credit-card.svg';
import { ReactComponent as RfidTagIcon } from '../../../assets/icons/rfid-icon.svg';
import { ReactComponent as StationIcon } from '../../../assets/icons/station-icon-view.svg';
import {
  CardPool,
  EmspStation,
  RfidToken,
  RfidTokenOption,
  UpdateCardPoolPayload,
} from '../../../interface/rfid-management.interface';
import { useAppDispatch } from '../../../redux/hooks';
import { updateCardPoolApi } from '../../../redux/slices/CardGroupSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import MultiSelectDropdown, {
  Option,
} from '../../../shared-components/multi-select-field/MultiSelectField';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import rfidApi from '../../../utils/api/rfid';

interface GroupDetailsModalProperties {
  group: CardPool;
  groupId?: string;
  onClose: () => void;
  onRecharge?: (group: CardPool) => void;
  onUpdate?: (updatedGroup: CardPool) => void;
  isEditing?: boolean;
  onEditModeChange?: (editing: boolean) => void;
}

/**
 * Safely extracts numeric balance in kWh from decimal object, number, or string
 * @param {any} balanceKwh - Raw energy balance value
 * @returns {number} Formatted numeric balance in kWh
 */
const getNumericBalance = (balanceKwh: any): number => {
  if (typeof balanceKwh === 'object' && balanceKwh !== null && '$numberDecimal' in balanceKwh) {
    return Number.parseFloat(balanceKwh.$numberDecimal) || 0;
  }
  if (typeof balanceKwh === 'number') {
    return balanceKwh;
  }
  if (typeof balanceKwh === 'string') {
    return Number.parseFloat(balanceKwh) || 0;
  }
  return 0;
};

/**
 * Resolves the full tenant network station ID (e.g. IN:ENR:1bfd0119-...)
 * @param {EmspStation} station - Station object
 * @returns {string} Fully qualified station ID
 */
const getFullStationId = (station: EmspStation): string => {
  const baseId = station.stationId || station.id || station._id || '';
  if (!baseId) return '';
  if (baseId.includes(':')) return baseId;
  const prefix = [station.countryCode, station.partyId].filter(Boolean).join(':');
  return prefix ? `${prefix}:${baseId}` : baseId;
};

/**
 * Details view and editor modal for a Card Group entity
 * @param {GroupDetailsModalProperties} properties - Component properties
 * @returns {React.JSX.Element} Card group details and editor modal content
 */
function GroupDetailsModal(properties: GroupDetailsModalProperties): React.JSX.Element {
  const {
    group,
    groupId: groupIdProperty,
    onClose,
    onRecharge,
    onUpdate,
    isEditing: isEditingProperty,
    onEditModeChange,
  } = properties;
  const { t, i18n } = useTranslation(['devices', 'sharedComponent']);
  const dispatch = useAppDispatch();

  const [currentGroup, setCurrentGroup] = useState<CardPool>(group);
  const [groupLoading, setGroupLoading] = useState<boolean>(false);
  const [stationList, setStationList] = useState<EmspStation[]>([]);
  const [stationsLoading, setStationsLoading] = useState<boolean>(false);
  const [allTokensList, setAllTokensList] = useState<RfidTokenOption[]>([]);
  const [tokensLoading, setTokensLoading] = useState<boolean>(false);
  const [internalEditing, setInternalEditing] = useState<boolean>(false);
  const isEditing = isEditingProperty !== undefined ? isEditingProperty : internalEditing;
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [name, setName] = useState<string>(group.name || '');
  const [selectedStationIds, setSelectedStationIds] = useState<string[]>(group.stationIds || []);
  const [selectedTokenIds, setSelectedTokenIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<{
    name?: string;
    selectedStationIds?: string;
    selectedTokenIds?: string;
  }>({});

  /**
   * Sets edit mode state internally and notifies parent component if callback is provided
   * @param {boolean} editing - Edit mode flag
   * @returns {void}
   */
  const handleSetEditing = (editing: boolean): void => {
    setInternalEditing(editing);
    if (onEditModeChange) {
      onEditModeChange(editing);
    }
  };

  /**
   * Extracts initial token identifier strings from a card group object
   * @param {any} groupData - Raw or formatted card group object
   * @returns {string[]} Array of token IDs or UIDs
   */
  const extractTokenIds = (groupData: any): string[] => {
    const rawTokens = groupData?.tokens || groupData?.cards || groupData?.tokenIds || [];
    if (Array.isArray(rawTokens)) {
      return rawTokens
        .map((item: any) => {
          if (typeof item === 'string') return item;
          return item?._id || item?.id || item?.uid || '';
        })
        .filter(Boolean);
    }
    return [];
  };

  useEffect(() => {
    setCurrentGroup(group);
    setName(group.name || '');
    setSelectedStationIds(group.stationIds || []);
    setSelectedTokenIds(extractTokenIds(group));
    setErrors({});
  }, [group]);

  useEffect(() => {
    let isMounted = true;
    const targetGroupId = groupIdProperty || group._id || group.id;

    if (targetGroupId) {
      setGroupLoading(true);
      rfidApi
        .fetchCardPoolById(targetGroupId)
        .then((response: any) => {
          if (!isMounted) return;
          const data =
            response?.data?.results || response?.data?.data || response?.data || response;
          if (data && typeof data === 'object') {
            const poolBalance = getNumericBalance(data.balanceKwh);
            const stationIds = data.stationIds || (data.stationId ? [data.stationId] : []);
            const stationNames = data.stationNames || [];
            const tokens = data.tokens || data.cards || [];

            const formattedGroup: CardPool = {
              ...data,
              id: data.id || data._id || targetGroupId,
              _id: data._id || data.id || targetGroupId,
              name: data.name ?? null,
              kind: data.kind || 'GROUP',
              tenantId: data.tenantId || '',
              balanceKwh: poolBalance,
              stationIds,
              stationNames,
              linkedCustomerId: data.linkedCustomerId || null,
              tokens,
              tokenCount: data.tokenCount ?? (Array.isArray(tokens) ? tokens.length : undefined),
              createdAt: data.createdAt ? ConvertUTCtoDate(data.createdAt) : '',
              updatedAt: data.updatedAt ? ConvertUTCtoDate(data.updatedAt) : '',
            };
            setCurrentGroup(formattedGroup);
            setName(formattedGroup.name || '');
            setSelectedStationIds(stationIds);
            setSelectedTokenIds(extractTokenIds(formattedGroup));
          }
        })
        .catch(() => {
          // Keep current group state on failure
        })
        .finally(() => {
          if (isMounted) {
            setGroupLoading(false);
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [group._id, group.id, groupIdProperty]);

  useEffect(() => {
    let isMounted = true;
    setStationsLoading(true);

    /**
     * Fetches available charging stations to resolve station names and operator details
     * @returns {Promise<void>} Resolves when stations are fetched
     */
    const loadStations = async (): Promise<void> => {
      try {
        const response = await rfidApi.fetchStationsList();
        if (isMounted) {
          const rawResults =
            response?.data?.results ||
            response?.data?.data ||
            (Array.isArray(response?.data) ? response?.data : []);
          const list: EmspStation[] = Array.isArray(rawResults) ? rawResults : [];
          setStationList(list);
        }
      } catch {
        // Gracefully fallback
      } finally {
        if (isMounted) {
          setStationsLoading(false);
        }
      }
    };

    loadStations();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setTokensLoading(true);

    /**
     * Fetches all RFID tokens from EMSP tokens/all API endpoint
     * @returns {Promise<void>} Resolves when tokens are loaded
     */
    const loadAllTokens = async (): Promise<void> => {
      try {
        const response = await rfidApi.fetchAllTokens();
        if (isMounted) {
          const rawResults =
            response?.data?.results ||
            response?.data?.data ||
            (Array.isArray(response?.data) ? response?.data : []);
          const list: RfidTokenOption[] = Array.isArray(rawResults) ? rawResults : [];
          setAllTokensList(list);
        }
      } catch {
        // Gracefully fallback
      } finally {
        if (isMounted) {
          setTokensLoading(false);
        }
      }
    };

    loadAllTokens();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (isEditing) {
      setName(currentGroup.name || '');
      setSelectedStationIds(currentGroup.stationIds || []);
      setSelectedTokenIds(extractTokenIds(currentGroup));
      setErrors({});
    }
  }, [isEditing]);

  const isGroup = currentGroup.kind === 'GROUP';
  const balanceKwh = getNumericBalance(currentGroup.balanceKwh);
  const stationNames = currentGroup.stationNames || [];

  const stationOptions: Option[] = stationList.map((item: EmspStation) => {
    const fullId = getFullStationId(item);
    const party = [item.countryCode, item.partyId].filter(Boolean).join(':');
    const displayName = item.name || fullId;
    const labelText = item.name
      ? `${item.name}${party ? ` (${party})` : ''} - ${fullId}`
      : `${fullId}`;

    return {
      ...item,
      label: labelText,
      value: fullId,
      labelNode: (
        <div className="rfid-customer-select-item">
          <span className="rfid-customer-select-item__name">{displayName}</span>
          <span className="rfid-customer-select-item__id">{fullId}</span>
          {party && <span className="rfid-customer-select-item__contact">{party}</span>}
        </div>
      ),
    };
  });

  const tokenOptions: Option[] = allTokensList.map((item: RfidTokenOption) => {
    const tokenDatabaseId = item._id || item.id || item.uid;
    const displayUid = item.uid || tokenDatabaseId;

    return {
      ...item,
      label: `${displayUid} (${tokenDatabaseId})`,
      value: tokenDatabaseId,
      labelNode: (
        <div className="rfid-customer-select-item">
          <span className="rfid-customer-select-item__name">{displayUid}</span>
          <span className="rfid-customer-select-item__id">{tokenDatabaseId}</span>
        </div>
      ),
    };
  });

  const selectedStations = selectedStationIds.map((stationId, index) => {
    const matchedStation = stationList.find(
      (s) =>
        getFullStationId(s) === stationId ||
        s.stationId === stationId ||
        s._id === stationId ||
        s.id === stationId,
    );
    let displayName = '';
    if (matchedStation?.name && matchedStation.name !== stationId) {
      displayName = matchedStation.name;
    } else if (stationNames && stationNames[index] && stationNames[index] !== stationId) {
      displayName = stationNames[index];
    }

    const parts = stationId.split(':');
    let party = '';
    if (matchedStation) {
      party = [matchedStation.countryCode, matchedStation.partyId].filter(Boolean).join(':');
    } else if (parts.length >= 3) {
      party = `${parts[0]}:${parts[1]}`;
    }

    return {
      id: stationId,
      name: displayName,
      party,
    };
  });

  const selectedTokens = selectedTokenIds.map((id) => {
    const matchedToken = allTokensList.find(
      (tok) => tok._id === id || tok.id === id || tok.uid === id,
    );
    const displayUid = matchedToken?.uid || id;
    const tokenDatabaseId = matchedToken?._id || matchedToken?.id || id;

    return {
      id: tokenDatabaseId,
      uid: displayUid,
    };
  });

  const tokensList: RfidToken[] = (currentGroup as any).tokens || (currentGroup as any).cards || [];

  /**
   * Cancels edit mode, restores initial group values, and closes the side panel
   * @returns {void}
   */
  const handleCancelEdit = (): void => {
    setName(currentGroup.name || '');
    setSelectedStationIds(currentGroup.stationIds || []);
    setSelectedTokenIds(extractTokenIds(currentGroup));
    setErrors({});
    handleSetEditing(false);
    onClose();
  };

  /**
   * Handles saving card group updates (name, stations, and assigned tokens)
   * @returns {Promise<void>} Resolves when save operation completes
   */
  const handleSave = async (): Promise<void> => {
    const targetGroupId = currentGroup.id || currentGroup._id || groupIdProperty || '';
    if (!targetGroupId) return;

    const newErrors: {
      name?: string;
      selectedStationIds?: string;
    } = {};

    if (!name.trim()) {
      newErrors.name = t('nameRequired', { defaultValue: 'Group name is required' });
    }

    if (!selectedStationIds || selectedStationIds.length === 0) {
      newErrors.selectedStationIds = t('stationRequired', {
        defaultValue: 'Please select at least one charging station',
      });
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstErrorMessage = Object.values(newErrors)[0];
      if (firstErrorMessage) {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            code: 400,
            message: firstErrorMessage,
          }),
        );
      }
      return;
    }

    setIsSaving(true);
    setErrors({});

    try {
      const payload: UpdateCardPoolPayload = {
        name: name.trim(),
        stationIds: selectedStationIds,
      };

      const updatedData = await dispatch(
        updateCardPoolApi({
          cardPoolId: targetGroupId,
          data: payload,
        }),
      ).unwrap();

      const initialTokenIds = extractTokenIds(currentGroup);
      const tokensToAdd = selectedTokenIds.filter((tokenId) => !initialTokenIds.includes(tokenId));
      const tokensToRemove = initialTokenIds.filter(
        (tokenId) => !selectedTokenIds.includes(tokenId),
      );

      if (tokensToAdd.length > 0 || tokensToRemove.length > 0) {
        const updatePromises: Promise<any>[] = [];
        tokensToAdd.forEach((tokenId) => {
          updatePromises.push(rfidApi.updateToken(tokenId, { cardPoolId: targetGroupId }));
        });
        tokensToRemove.forEach((tokenId) => {
          updatePromises.push(rfidApi.updateToken(tokenId, { cardPoolId: null }));
        });
        await Promise.allSettled(updatePromises);
      }

      const updatedGroup: CardPool = {
        ...currentGroup,
        ...updatedData,
        name: name.trim(),
        stationIds: selectedStationIds,
        tokenCount: selectedTokenIds.length,
        tokens: selectedTokens as any,
      };

      setCurrentGroup(updatedGroup);
      handleSetEditing(false);

      if (onUpdate) {
        onUpdate(updatedGroup);
      }
    } catch {
      // Error notification toast is dispatched automatically by updateCardPoolApi
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Renders the charging stations content for the group in view mode
   * @returns {React.JSX.Element} Stations list or empty state
   */
  const renderStationsViewContent = (): React.JSX.Element => {
    if (stationsLoading) {
      return (
        <div className="rfid-stations-loading">
          <span className="rfid-stations-loading__spinner" />
          <span>{t('loadingStations', { defaultValue: 'Loading charging stations...' })}</span>
        </div>
      );
    }

    if (selectedStations.length === 0) {
      return (
        <div className="rfid-empty-card">
          <p className="rfid-empty-card__text">
            {t('noStationsAssigned', {
              defaultValue: 'No charging stations currently assigned.',
            })}
          </p>
        </div>
      );
    }

    return (
      <div className="rfid-selected-stations-list">
        <div className="rfid-selected-stations-list__items">
          {selectedStations.map((station) => (
            <div key={station.id} className="rfid-station-item-card">
              <div className="rfid-station-item-card__icon">
                <StationIcon />
              </div>
              <div className="rfid-station-item-card__info">
                {station.name ? (
                  <>
                    <span className="rfid-station-item-card__name">{station.name}</span>
                    <div className="rfid-station-item-card__sub-row">
                      <span className="rfid-station-item-card__id">{station.id}</span>
                      {station.party && (
                        <span className="rfid-station-item-card__party">{station.party}</span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="rfid-station-item-card__sub-row">
                    <span className="rfid-station-item-card__id">{station.id}</span>
                    {station.party && (
                      <span className="rfid-station-item-card__party">{station.party}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Renders the assigned RFID cards content for the group in view mode
   * @returns {React.JSX.Element} RFID tokens list or empty state
   */
  const renderTokensViewContent = (): React.JSX.Element => {
    if (tokensLoading) {
      return (
        <div className="rfid-stations-loading">
          <span className="rfid-stations-loading__spinner" />
          <span>{t('loadingTokens', { defaultValue: 'Loading RFID tokens...' })}</span>
        </div>
      );
    }

    if (tokensList.length === 0 && selectedTokens.length === 0) {
      return (
        <div className="rfid-empty-card">
          <p className="rfid-empty-card__text">
            {t('noCardsAssigned', {
              defaultValue: 'No RFID cards currently assigned to this group.',
            })}
          </p>
        </div>
      );
    }

    const displayList =
      tokensList.length > 0
        ? tokensList
        : selectedTokens.map((item) => ({
            id: item.id,
            _id: item.id,
            uid: item.uid,
            status: 'ACTIVE',
          }));

    return (
      <div className="rfid-selected-stations-list">
        <div className="rfid-selected-stations-list__items">
          {displayList.map((tok: any) => {
            const tokId = tok.id || tok._id || tok.uid;
            const isTokActive = tok.status === 'ACTIVE' || !tok.status;
            return (
              <div key={tokId} className="rfid-selected-user-card">
                <div className="rfid-selected-user-card__header">
                  <div className="rfid-selected-user-card__title-group">
                    <span className="rfid-selected-user-card__avatar">
                      <RfidTagIcon />
                    </span>
                    <span className="rfid-selected-user-card__name">{tok.uid || tokId}</span>
                  </div>
                  <span
                    className={`rfid-status-badge ${
                      isTokActive ? 'rfid-status-badge--active' : 'rfid-status-badge--deactivated'
                    }`}>
                    <span className="rfid-status-badge__dot" />
                    {tok.status || 'ACTIVE'}
                  </span>
                </div>
                {tok.createdAt && (
                  <div className="rfid-selected-user-card__details">
                    <div className="rfid-selected-user-card__detail-item">
                      <span className="rfid-selected-user-card__detail-label">
                        {t('createdDate', { defaultValue: 'Created Date' })}:
                      </span>
                      <span className="rfid-selected-user-card__detail-value">
                        {ConvertUTCtoDateTime(tok.createdAt) || tok.createdAt}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (groupLoading && !currentGroup.id && !currentGroup._id) {
    return (
      <div className="rfid-entity-details" dir={i18n.dir()}>
        <div className="rfid-entity-details__body">
          <div className="rfid-stations-loading">
            <span className="rfid-stations-loading__spinner" />
            <span>
              {t('loadingGroupDetails', { defaultValue: 'Loading Card Group details...' })}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rfid-entity-details" dir={i18n.dir()}>
      <div className="rfid-entity-details__body">
        {/* ── Header Banner ─────────────────────────────────── */}
        <div className="rfid-entity-details__header">
          <div className="rfid-entity-details__avatar rfid-entity-details__avatar--purple">
            <CardIcon />
          </div>
          <div className="rfid-entity-details__header-text">
            <h3>
              {name ||
                currentGroup.name ||
                (currentGroup.kind === 'INDIVIDUAL' ? t('individualTapGroup', { defaultValue: 'Individual Tap Group' }) : t('cardGroup', { defaultValue: 'Card Group' }))}
            </h3>
            <div className="rfid-entity-details__badges">
              <span
                className={`rfid-kind-badge ${
                  isGroup ? 'rfid-kind-badge--group' : 'rfid-kind-badge--individual'
                }`}>
                {currentGroup.kind === 'GROUP' ? t('cardGroup', { defaultValue: 'Card Group' }) : t('individual', { defaultValue: 'Individual' })}
              </span>
              <span className="rfid-balance-tag">{balanceKwh.toFixed(2)} kWh</span>
            </div>
          </div>
        </div>

        {/* ── Edit Form or View Information Grid ────────────── */}
        {isEditing ? (
          <div className="rfid-modal-form__field">
            <CustomInput
              inputLabel={t('groupName', { defaultValue: 'Group Name' })}
              type="text"
              name="name"
              placeholder="e.g. Corporate Fleet East, Logistics Vans"
              value={name}
              valueRequired
              error={Boolean(errors.name)}
              errorMessage={errors.name}
              onChangeValue={(_, value) => {
                setName(String(value));
                if (errors.name) {
                  setErrors((previous) => ({ ...previous, name: undefined }));
                }
              }}
            />
          </div>
        ) : (
          <div className="rfid-entity-details__grid">
            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('groupId', { defaultValue: 'Group ID' })}
              </span>
              <span className="rfid-entity-details__value rfid-entity-details__mono">
                {currentGroup.id || currentGroup._id}
              </span>
            </div>

            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('owningTenantId', { defaultValue: 'Owning Tenant ID' })}
              </span>
              <span className="rfid-entity-details__value rfid-entity-details__mono">
                {currentGroup.tenantId || '—'}
              </span>
            </div>

            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('sharedBalance', { defaultValue: 'Shared Energy Balance' })}
              </span>
              <span className="rfid-entity-details__value numeric-unit" dir="ltr">
                {balanceKwh.toFixed(2)} kWh
              </span>
            </div>

            {currentGroup.linkedCustomerId && (
              <div className="rfid-entity-details__item">
                <span className="rfid-entity-details__label">
                  {t('linkedCustomerId', { defaultValue: 'Linked Customer' })}
                </span>
                <span className="rfid-entity-details__value rfid-entity-details__mono">
                  {currentGroup.linkedCustomerId}
                </span>
              </div>
            )}

            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('cardsCount', { defaultValue: 'Cards Count' })}
              </span>
              <span className="rfid-entity-details__value">
                {selectedTokenIds.length || currentGroup.tokenCount || 0}{' '}
                {t('cards', { defaultValue: 'Cards' })}
              </span>
            </div>

            <div className="rfid-entity-details__item">
              <span className="rfid-entity-details__label">
                {t('createdDate', { defaultValue: 'Created Date' })}
              </span>
              <span className="rfid-entity-details__value">{currentGroup.createdAt || '—'}</span>
            </div>

            {currentGroup.updatedAt && (
              <div className="rfid-entity-details__item">
                <span className="rfid-entity-details__label">
                  {t('lastUpdated', { defaultValue: 'Last Updated' })}
                </span>
                <span className="rfid-entity-details__value">{currentGroup.updatedAt}</span>
              </div>
            )}
          </div>
        )}

        {/* ── Authorized Charging Stations Section ──────────── */}
        <div className="rfid-entity-details__section">
          <div className="rfid-entity-details__section-header">
            <span className="rfid-entity-details__section-title">
              <StationIcon />
              {isEditing
                ? t('editAuthorizedStations', {
                    defaultValue: 'Authorized Charging Stations',
                  })
                : t('authorizedStations', { defaultValue: 'Authorized Charging Stations' })}
              {!isEditing && (
                <span className="rfid-entity-details__count-pill">{selectedStationIds.length}</span>
              )}
            </span>
          </div>

          {isEditing ? (
            <div className="rfid-edit-stations-container">
              <MultiSelectDropdown
                title={t('chargingStations', { defaultValue: 'Charging Stations' })}
                name="selectedStationIds"
                options={stationOptions}
                selectedValues={selectedStationIds}
                drillDownLevels={['countryCode', 'partyId']}
                disabled={stationsLoading}
                valueRequired
                error={Boolean(errors.selectedStationIds)}
                errorMessage={errors.selectedStationIds}
                onSelectionChange={(_, values) => {
                  setSelectedStationIds(values);
                  if (errors.selectedStationIds) {
                    setErrors((previous) => ({ ...previous, selectedStationIds: undefined }));
                  }
                }}
                placeholder={
                  stationsLoading
                    ? t('loadingStations', { defaultValue: 'Loading charging stations...' })
                    : t('selectStationsPlaceholder', {
                        defaultValue: 'Select one or more charging stations',
                      })
                }
                placeholderFilled={
                  selectedStationIds.length > 1
                    ? `${selectedStationIds.length} ${t('stations', { defaultValue: 'stations' })}`
                    : `1 ${t('station', { defaultValue: 'station' })}`
                }
                search
              />
              {errors.selectedStationIds && (
                <span className="text-danger">{errors.selectedStationIds}</span>
              )}

              {selectedStations.length > 0 && (
                <div className="rfid-selected-stations-list">
                  <div className="rfid-selected-stations-list__header">
                    <span className="rfid-selected-stations-list__title">
                      {t('selectedStationsCount', {
                        defaultValue: 'Selected Stations',
                      })}{' '}
                      ({selectedStations.length})
                    </span>
                    {selectedStations.length > 1 && (
                      <button
                        type="button"
                        className="rfid-selected-stations-list__clear-btn"
                        onClick={() => setSelectedStationIds([])}>
                        {t('clearAll', { defaultValue: 'Clear all' })}
                      </button>
                    )}
                  </div>
                  <div className="rfid-selected-stations-list__items">
                    {selectedStations.map((station) => (
                      <div key={station.id} className="rfid-selected-user-card">
                        <div className="rfid-selected-user-card__header">
                          <span className="rfid-selected-user-card__name">
                            {station.name || station.id}
                          </span>
                          <div className="rfid-selected-user-card__header-right">
                            {station.name && (
                              <span className="rfid-selected-user-card__id">{station.id}</span>
                            )}
                            <button
                              type="button"
                              className="rfid-selected-user-card__remove-btn"
                              onClick={() =>
                                setSelectedStationIds((previous) =>
                                  previous.filter((id) => id !== station.id),
                                )
                              }
                              aria-label={`Remove ${station.name || station.id}`}
                              title={t('remove', { defaultValue: 'Remove' })}>
                              ✕
                            </button>
                          </div>
                        </div>
                        {station.party && (
                          <div className="rfid-selected-user-card__details">
                            <div className="rfid-selected-user-card__detail-item">
                              <span className="rfid-selected-user-card__detail-label">
                                {t('operator', { defaultValue: 'Party / Operator' })}:
                              </span>
                              <span className="rfid-selected-user-card__detail-value">
                                {station.party}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            renderStationsViewContent()
          )}
        </div>

        {/* ── Assigned RFID Cards Section ───────────────────── */}
        <div className="rfid-entity-details__section">
          <div className="rfid-entity-details__section-header">
            <span className="rfid-entity-details__section-title">
              <RfidTagIcon />
              {isEditing
                ? t('editAssignedCards', { defaultValue: 'Assigned RFID Cards' })
                : t('assignedCards', { defaultValue: 'Assigned RFID Cards' })}
              {!isEditing && (
                <span className="rfid-entity-details__count-pill">
                  {selectedTokenIds.length || tokensList.length}
                </span>
              )}
            </span>
          </div>

          {isEditing ? (
            <div className="rfid-edit-stations-container">
              <MultiSelectDropdown
                title={t('rfidCards', { defaultValue: 'RFID Cards' })}
                name="selectedTokenIds"
                options={tokenOptions}
                selectedValues={selectedTokenIds}
                disabled={tokensLoading}
                error={Boolean(errors.selectedTokenIds)}
                errorMessage={errors.selectedTokenIds}
                onSelectionChange={(_, values) => {
                  setSelectedTokenIds(values);
                  if (errors.selectedTokenIds) {
                    setErrors((previous) => ({ ...previous, selectedTokenIds: undefined }));
                  }
                }}
                placeholder={
                  tokensLoading
                    ? t('loadingTokens', { defaultValue: 'Loading RFID tokens...' })
                    : t('selectRfidTokensPlaceholder', {
                        defaultValue: 'Select one or more RFID cards',
                      })
                }
                placeholderFilled={
                  selectedTokenIds.length > 1
                    ? `${selectedTokenIds.length} ${t('cards', { defaultValue: 'cards' })}`
                    : `1 ${t('card', { defaultValue: 'card' })}`
                }
                search
              />
              {errors.selectedTokenIds && (
                <span className="text-danger">{errors.selectedTokenIds}</span>
              )}

              {selectedTokens.length > 0 && (
                <div className="rfid-selected-stations-list">
                  <div className="rfid-selected-stations-list__header">
                    <span className="rfid-selected-stations-list__title">
                      {t('selectedCardsCount', {
                        defaultValue: 'Selected RFID Cards',
                      })}{' '}
                      ({selectedTokens.length})
                    </span>
                    {selectedTokens.length > 1 && (
                      <button
                        type="button"
                        className="rfid-selected-stations-list__clear-btn"
                        onClick={() => setSelectedTokenIds([])}>
                        {t('clearAll', { defaultValue: 'Clear all' })}
                      </button>
                    )}
                  </div>
                  <div className="rfid-selected-stations-list__items">
                    {selectedTokens.map((tok) => (
                      <div key={tok.id} className="rfid-selected-user-card">
                        <div className="rfid-selected-user-card__header">
                          <div className="rfid-selected-user-card__title-group">
                            <span className="rfid-selected-user-card__avatar">
                              <RfidTagIcon />
                            </span>
                            <span className="rfid-selected-user-card__name">{tok.uid}</span>
                          </div>
                          <div className="rfid-selected-user-card__header-right">
                            <span className="rfid-selected-user-card__id">{tok.id}</span>
                            <button
                              type="button"
                              className="rfid-selected-user-card__remove-btn"
                              onClick={() =>
                                setSelectedTokenIds((previous) =>
                                  previous.filter((id) => id !== tok.id),
                                )
                              }
                              aria-label={`Remove ${tok.uid}`}
                              title={t('remove', { defaultValue: 'Remove' })}>
                              ✕
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            renderTokensViewContent()
          )}
        </div>
      </div>

      {/* ── Bottom Sticky Actions ──────────────────────────── */}
      <div className="rfid-entity-details__actions">
        {isEditing ? (
          <div className="rfid-entity-details__actions--edit">
            <MyButton
              label={
                isSaving
                  ? t('saving', { defaultValue: 'Saving...' })
                  : t('saveChanges', { defaultValue: 'Save Changes' })
              }
              buttonType="primary"
              onClickFunc={handleSave}
              disableButton={isSaving || stationsLoading || tokensLoading}
              loading={isSaving}
            />
            <MyButton
              label={t('cancel', { defaultValue: 'Cancel' })}
              buttonType="outline"
              onClickFunc={handleCancelEdit}
              disableButton={isSaving}
            />
          </div>
        ) : (
          <div className="rfid-entity-details__actions--buttons">
            {onRecharge && (
              <MyButton
                label={t('rechargeBalance', { defaultValue: 'Recharge Energy (kWh)' })}
                buttonType="primary"
                onClickFunc={() => {
                  onRecharge(currentGroup);
                }}
              />
            )}
            <MyButton
              label={t('close', { defaultValue: 'Close' })}
              buttonType="outline"
              onClickFunc={onClose}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default GroupDetailsModal;
